import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { auth, db } from "../components/firebase";
import "../styles/recipegen.css";

const MultiSelectDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (itemName) => {
    const cleanItem = itemName.trim();
    setSelected((prev) => (prev.includes(cleanItem) ? prev.filter((i) => i !== cleanItem) : [...prev, cleanItem]));
  };

  // Load pantry items
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const pantryRef = collection(db, "PantryList", user.uid, "pantry");
    const q = query(pantryRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map((doc) => doc.data().name);
      setOptions(items);
    });

    return () => unsubscribe();
  }, []);

  // Generate recipes
  const handleGenerateRecipe = async (e) => {
    e.preventDefault();
    if (!selected.length) {
      alert("Please select at least one pantry item.");
      return;
    }

    setLoading(true);
    setRecipes([]);

    try {
      const res = await fetch(`${process.env.REACT_APP_FUNCTIONS_BASE_URL}/generateRecipe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ingredients: selected }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to generate recipes");

      const aiText = data.predictions?.[0]?.content || "";

      // Parse JSON safely
      const parsedRecipes = (() => {
        try {
          const jsonStart = aiText.indexOf("{");
          const jsonEnd = aiText.lastIndexOf("}") + 1;
          const jsonString = aiText.substring(jsonStart, jsonEnd);
          const recipeObj = JSON.parse(jsonString);
          return Array.isArray(recipeObj) ? recipeObj : [recipeObj];
        } catch {
          // fallback parsing if AI didn't return clean JSON
          return aiText.split("\n\n").map((text, index) => ({
            "Recipe Name": text.match(/Recipe Name:(.*)/)?.[1]?.trim() || `Recipe ${index + 1}`,
            Description: text.match(/Description:(.*)/)?.[1]?.trim() || "",
            Ingredients:
              text
                .match(/Ingredients:(.*)/)?.[1]
                ?.split(",")
                .map((i) => i.trim()) || [],
            Instructions:
              text
                .match(/Instructions:(.*)/)?.[1]
                ?.split(".")
                .map((step) => step.trim())
                .filter(Boolean) || [],
          }));
        }
      })();

      setRecipes(parsedRecipes.length ? parsedRecipes : []);
    } catch (error) {
      console.error("Error generating recipes:", error);
      alert("Failed to generate recipes. Showing fallback recipes.");
      setRecipes([
        {
          "Recipe Name": "Quick Veggie Stir Fry",
          Description: "Easy stir fry with pantry veggies",
          Ingredients: ["1 cup carrots", "1 cup broccoli", "1 tbsp soy sauce"],
          Instructions: ["Chop vegetables", "Heat oil in a pan", "Stir fry with soy sauce for 5-7 minutes"],
        },
        {
          "Recipe Name": "Simple Fruit Salad",
          Description: "Refreshing fruit salad from pantry fruits",
          Ingredients: ["1 apple", "1 banana", "1 tbsp honey"],
          Instructions: ["Chop fruits", "Mix with honey", "Serve fresh and chilled"],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleGenerateRecipe} className="recipe-form">
        <div className="dropdown-container">
          <div className="dropdown-header" onClick={toggleDropdown}>
            {selected.length > 0 ? selected.join(", ") : "Select pantry items"}
            <span className={`arrow ${isOpen ? "open" : ""}`}>▼</span>
          </div>
          {isOpen && (
            <div className="dropdown-list">
              {options.length > 0 ? (
                options.map((item) => (
                  <label key={item} className="dropdown-item">
                    <input type="checkbox" checked={selected.includes(item.trim())} onChange={() => handleSelect(item)} />
                    {item}
                  </label>
                ))
              ) : (
                <p>No pantry items found.</p>
              )}
            </div>
          )}
        </div>
        <button type="submit" className="btn btn-success" disabled={loading}>
          {loading ? "Generating..." : "Generate Recipe"}
        </button>
      </form>

      {recipes.length > 0 && (
        <div className="generated-recipes">
          <h2>Delicious Ideas, Curated for You!!</h2>
          <br />
          {recipes.map((r, index) => (
            <div key={index} className="recipe-card">
              <h3>{r["Recipe Name"]}</h3>
              <p className="recipe-description">{r.Description}</p>
              <div className="recipe-ingredients">
                <h4>Ingredients:</h4>
                <ul>
                  {r.Ingredients.map((ing, i) => (
                    <li key={i}>{ing}</li>
                  ))}
                </ul>
              </div>
              <div className="recipe-instructions">
                <h4>Instructions:</h4>
                <ol>
                  {r.Instructions.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default MultiSelectDropdown;
